import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import { useTripActions } from "../../state/trip-context";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import type { Trip } from "../../types";
import { TripPartyFields, TripStartField } from "./trip-when-fields";
import { partyOf, partySize, type TripParty } from "../../lib/trip-party";

/**
 * Change when a trip starts and who is coming, after it was made.
 *
 * The days keep their stops: moving day 1 moves every day with it. This is
 * also the way out for a trip saved before trips had dates, whose first day
 * is the day it was created and so is usually behind us.
 */
export function TripWhenDialog({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const { t } = useTranslation();
  const { setTripWhen } = useTripActions();
  const [start, setStart] = useState<string | null>(trip.startDate ?? null);
  const [party, setParty] = useState<TripParty>(() => partyOf(trip, DEFAULT_TRIP_PEOPLE));
  const dialogRef = useRef<HTMLDivElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(dialogRef, true, saveRef);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!start) return;
    setTripWhen(trip.id, start, partySize(party), party.childAges);
    onClose();
  };

  return createPortal(
    <div
      className="mj-trip-dialog-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="mj-trip-dialog mj-trip-dialog--create"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mj-trip-when-title"
      >
        <header className="mj-trip-dialog__header">
          <div>
            <span>{trip.name}</span>
            <strong id="mj-trip-when-title">{t("trip.book.change")}</strong>
          </div>
          <button
            type="button"
            className="mj-trip-dialog__close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <Icon name="ri-close-line" aria-hidden="true" />
          </button>
        </header>
        <form className="mj-trip-dialog__form" onSubmit={save}>
          <TripStartField value={start} onChange={setStart} />
          <TripPartyFields value={party} onChange={setParty} />
          <footer className="mj-trip-dialog__footer">
            <button type="button" className="mj-trip-dialog__cancel" onClick={onClose}>
              {t("trip.cancel")}
            </button>
            <button
              ref={saveRef}
              type="submit"
              className="mj-trip-dialog__submit mj-press"
              disabled={!start}
            >
              <Icon name="ri-check-line" aria-hidden="true" />
              {t("trip.time.save")}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
